/**************
 * FACILITIES *
 *************/

/**
 * Get a single facility by id.
 * @return {function} - Callable that returns the facility by passing an id.
 * @example:
 *      // returns facility with id 3
 *      this.$store.getters['facilities/getFacility'](3)
 */
export function getFacility(state) {
    return id => {
        if (state.facilities && state.facilities[id]) {
            return state.facilities[id];
        }
        return null;
    };
}

/**
 * Get a single facility group by id.
 * @return {function} - Callable that returns the facility group by passing an id.
 * @example:
 *      // returns facility group with id 3
 *      this.$store.getters['facilities/getFacilityGroup'](3)
 */
export function getFacilityGroup(state) {
    return id => {
        if (state.facilityGroups && state.facilityGroups[id]) {
            return state.facilityGroups[id];
        }
        return null;
    };
}

/**
 * Get the current facility selected.
 * @return {(Object|null)}
 */
export function getSelectedFacility(state) {
    if (
        state.selectedFacility &&
        state.facilities &&
        state.facilities[state.selectedFacility]
    ) {
        return state.facilities[state.selectedFacility];
    }
    return null;
}

/**
 * Get list of facilities.
 * @return {(Object[]|null)} - List of facilities.
 */
export function getFacilities(state) {
    if (state.facilities === null) {
        return null;
    } else {
        return Object.values(state.facilities).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
    }
}

/**
 * Get list of facility groups that have facilities.
 * @param {Object} state - The state object.
 * @return {Object[]} - List of facility groups that have facilities.
 */
export function getFacilityGroups(state) {
    if (
        !state.facilityGroups ||
        Object.keys(state.facilityGroups).length === 0
    ) {
        return [];
    } else {
        return Object.values(state.facilityGroups)
            .filter(group => group.facilities) // Only include facility groups
            .sort((a, b) => a.name.localeCompare(b.name));
    }
}

/**
 * Check if a request to load assets for a facility has already been attempted.
 * @param {Set} - Facility ids.
 */
export function checkAssetsLoadingFailed(state) {
    return facilityId => {
        return parseInt(facilityId) in state.failedAssetLoads;
    };
}

/**********
 * ASSETS *
 **********/

/**
 * Get asset list from a specific facility.
 * @return {function} - Callable that returns assets by passing the facility id.
 */
export function getAssets(state) {
    return function (facilityId, withDeleted = false) {
        const assets = Object.values(state.assets).sort((a, b) =>
            a.name.localeCompare(b.name)
        );
        // Delete assets are not included by default
        if (!withDeleted) {
            return assets.filter(
                asset =>
                    asset.facility === parseInt(facilityId) && !asset.is_deleted
            );
        }

        // Return all assets including deleted
        return assets.filter(asset => asset.facility === facilityId);
    };
}

/**
 * Get a single asset by id.
 * @return {function} - Callable that returns an asset by passing the asset id.
 */
export function getAsset(state) {
    // return a callable that will return the asset by passing an id
    return id => {
        if (state.assets[id]) {
            return state.assets[id];
        } else {
            return null;
        }
    };
}

/**
 * Get a single asset by UUID.
 * @return {function} - Callable that returns an asset by passing the asset UUID.
 */
export function getAssetByUUID(state) {
    // return a callable that will return the asset by passing an UUID
    return uuid => {
        for (const asset of Object.values(state.assets)) {
            if (asset.uuid === uuid) {
                return asset;
            }
        }
        return null;
    };
}

/**
 * Retrieves the name of an asset given its ID.
 *
 * @param {Object} state - The state object containing assets.
 * @returns {Function} A function that takes an asset ID and returns the asset name if it exists, otherwise null.
 */
export function getAssetName(state) {
    return assetId => {
        if (state.assets[assetId]) {
            return state.assets[assetId].name;
        } else {
            return null;
        }
    };
}

/**
 * Get the current facility selected.
 * @return {(Object|null)}
 */
export function getSelectedAsset(state) {
    if (state.selectedAsset) {
        return state.assets[state.selectedAsset];
    } else {
        return null;
    }
}

/**
 * Get asset phase details.
 * @return {function} - Callable that returns an asset's phase details by id.
 */
export function getAssetPhase(state) {
    return id => {
        // check if this assetId has been loaded
        return state.statuses[id] || null;
    };
}

/**
 * Get asset status by asset id.
 * @return {function} - Callable that returns an asset's status by id.
 */
export function getAssetStatus(state) {
    return id => {
        // check if this assetId has been loaded
        if (state.statuses[id]) {
            return state.statuses[id]['status'];
        } else {
            return null;
        }
    };
}

/**
 * Get current product running on an asset by asset id.
 * @return {function} - Callable that returns the current product by an asset's id.
 */
export function getAssetProduct(state) {
    return id => {
        // check if this assetId has been loaded
        if (state.statuses[id]) {
            return state.statuses[id]['currentProduct'];
        } else {
            return null;
        }
    };
}

/**
 * Get the current cause of the asset
 * @return {function} - Callable that returns the current cause by an asset's id.
 */
export function getAssetCause(state) {
    return id => {
        // check if this assetId has been loaded
        if (state.statuses[id]) {
            return state.statuses[id]['cause'];
        } else {
            return null;
        }
    };
}

/**
 * Get quality event for an asset by asset id.
 * @return {function} - Callable that returns the quality events by an asset's id.
 */
export function getQualityEvent(state) {
    return assetId => {
        // check if this assetId has been loaded
        if (state.qualityEvents[assetId]) {
            return state.qualityEvents[assetId];
        } else {
            return null;
        }
    };
}

/**
 * Get sensors running on an asset by asset id.
 * @return {function} - Callable that returns the sensors by an asset's id.
 */
export function getSensors(state) {
    return id => {
        // check if this assetId has been loaded
        if (state.sensors[id]) {
            return state.sensors[id];
        } else {
            return null;
        }
    };
}
/**
 * Get Asset groups of assets in the facility.
 * @return {function} - Callable that returns the Asset Groups by an facility id.
 */
export function getAssetGroups(state) {
    return function (facilityId) {
        // select the groups that belong to this facility
        const groups = Object.values(state.assetGroups).filter(group => {
            return parseInt(group.facility) === parseInt(facilityId);
        });

        // order the list by name
        return groups.sort((a, b) => a.name.localeCompare(b.name));
    };
}

/**
 * Retrieve an asset group from store
 * @return {function} - Callable that returns the Asset Group for a given group id
 */
export function getGroup(state) {
    return function (groupId) {
        // order the list by name
        return state.assetGroups[groupId];
    };
}

/**
 * Retrieve stored asset groups for
 * particular facility.
 * @return  {function} - Callable that returns the stored asset groups
 * for the selected facility.
 */
export function getStoredAssetGroupsPerFacility(state) {
    return function (facilityId) {
        const storedAssetGroupsPerFacility =
            state.storedAssetGroupsPerFacility[facilityId];
        if (storedAssetGroupsPerFacility) {
            return storedAssetGroupsPerFacility;
        }
        return [];
    };
}

/**
 * Get asset disconnection details.
 * @param {state} state - Vuex state for facilities
 * @returns {function} - Callable that returns the asset disconnection details object.
 */
export function getDeviceDisconnection(state) {
    return assetId => {
        if (state.disconnectedAssets.has(assetId)) {
            return state.disconnectedAssets.get(assetId);
        }
        return null;
    };
}

/**
 * Check if an asset is connected to at least one sensor.
 * @param {state} state - Vuex state for facilities
 * @returns {function} - Callable that returns whether an asset is
 * connected to a sensor.
 */
export function isAssetConnectedToSensor(state) {
    return assetId => {
        return state.assetsWithSensors.has(assetId);
    };
}

/***********
 * DEFECTS *
 ***********/

/**
 * Retrieves a defect from the state by its ID.
 *
 * @param {Object} state - The state object containing defects.
 * @returns {Function} A function that takes an ID and returns the corresponding defect if it exists, or null if it does not.
 */
export function getDefect(state) {
    return id => {
        if (id in state.defects) {
            return state.defects[id];
        } else {
            return null;
        }
    };
}
